<script lang="ts" module>
  import { uniqueContext } from "#lib/internal/helpers"
  import type { BaseProps } from "#lib/internal/types"

  export type TooltipRootProps = BaseProps & {
    open?: boolean
    delayDuration?: number
    disableHoverableContent?: boolean
    children?: any
  }

  export type TooltipRootContext = {
    contentId: string
    open: boolean
    stateAttribute: "closed" | "instant-open" | "delayed-open"
    trigger: HTMLButtonElement | null
    onTriggerEnter: () => void
    onTriggerLeave: () => void
    onOpen: () => void
    onClose: () => void
    disableHoverableContent: boolean
  }

  const { getContext, setContext } = uniqueContext<TooltipRootContext>()
  export const getTooltipRootContext = getContext
</script>

<script lang="ts">
  import { Popper } from "#lib/components/ui/popper/index"
  import { generateId } from "#lib/internal/helpers"
  import { onDestroy, type Snippet } from "svelte"
  import { getTooltipProviderContext } from "./TooltipProvider.svelte"
  import { TOOLTIP_OPEN } from "./constants"

  interface Props {
    open?: boolean
    delayDuration?: number
    disableHoverableContent?: boolean
    use?: any[]
    children?: Snippet
    [key: string]: any
  }

  let {
    open = $bindable(false),
    delayDuration = $bindable(undefined),
    disableHoverableContent = $bindable(undefined),
    use = [],
    children,
    ...rest
  }: Props = $props()

  const providerCtx = getTooltipProviderContext()

  $effect(() => {
    if (disableHoverableContent === undefined) {
      disableHoverableContent = providerCtx?.disableHoverableContent ?? false
    }
  })
  $effect(() => {
    if (delayDuration === undefined) {
      delayDuration = providerCtx?.delayDuration ?? 300
    }
  })

  let openTimer = 0
  let wasOpenDelayed = $state(false)
  let trigger: HTMLButtonElement | null = $state(null)

  function setOpen(v: boolean) {
    if (v) {
      providerCtx?.onOpen()
      document.dispatchEvent(new CustomEvent(TOOLTIP_OPEN))
    } else {
      providerCtx?.onClose()
    }
    open = v
  }

  const handleOpen = () => {
    window.clearTimeout(openTimer)
    wasOpenDelayed = false
    setOpen(true)
  }

  const handleClose = () => {
    window.clearTimeout(openTimer)
    setOpen(false)
  }

  const handleDelayedOpen = () => {
    window.clearTimeout(openTimer)
    openTimer = window.setTimeout(() => {
      wasOpenDelayed = true
      setOpen(true)
    }, delayDuration)
  }

  const onTriggerEnter = () => {
    if (providerCtx?.isOpenDelayed) {
      handleDelayedOpen()
    } else {
      handleOpen()
    }
  }

  const onTriggerLeave = () => {
    if (disableHoverableContent) {
      handleClose()
    } else {
      window.clearTimeout(openTimer)
    }
  }

  onDestroy(() => {
    window.clearTimeout(openTimer)
  })

  const stateAttribute = $derived.by(
    (): TooltipRootContext["stateAttribute"] => {
      if (!open) return "closed"
      if (wasOpenDelayed) return "delayed-open"
      return "instant-open"
    }
  )

  setContext({
    contentId: generateId(),
    get open() {
      return open
    },
    set open(v) {
      setOpen(v)
    },
    get stateAttribute() {
      return stateAttribute
    },
    get trigger() {
      return trigger
    },
    set trigger(v) {
      trigger = v
    },
    get onTriggerEnter() {
      return onTriggerEnter
    },
    get onTriggerLeave() {
      return onTriggerLeave
    },
    get onOpen() {
      return handleOpen
    },
    get onClose() {
      return handleClose
    },
    get disableHoverableContent() {
      return disableHoverableContent ?? false
    },
  })
</script>

<Popper.Root {use} {...rest}>
  {@render children?.()}
</Popper.Root>
